import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { FiActivity, FiCheckCircle, FiAlertTriangle, FiXCircle, FiRefreshCw, FiClock, FiChevronDown, FiChevronRight, FiMail } from 'react-icons/fi';
import { systemHealthAPI } from '../services/api';

const STATUS = {
  pass: { color: '#10b981', bg: '#ecfdf5', label: 'Pass', icon: FiCheckCircle },
  warn: { color: '#f59e0b', bg: '#fffbeb', label: 'Warning', icon: FiAlertTriangle },
  fail: { color: '#ef4444', bg: '#fef2f2', label: 'Fail', icon: FiXCircle },
};
const OVERALL = {
  healthy: { color: '#10b981', label: 'All systems healthy', icon: FiCheckCircle },
  degraded: { color: '#f59e0b', label: 'Degraded: some checks need attention', icon: FiAlertTriangle },
  unhealthy: { color: '#ef4444', label: 'Unhealthy: failing checks', icon: FiXCircle },
};
const CATEGORY_ORDER = ['Infrastructure', 'Configuration', 'Payments', 'Integrations', 'Users & Workspaces', 'Data', 'Application'];

const timeAgo = (d) => {
  if (!d) return 'never';
  const mins = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} days ago`;
};

const describeCron = (cron, tz) => {
  const m = /^(\d+) (\d+) \* \* \*$/.exec(cron || '');
  return m ? `Daily at ${m[2].padStart(2, '0')}:${m[1].padStart(2, '0')} (${tz})` : `${cron} (${tz})`;
};

const card = { background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' };

const CheckRow = ({ check }) => {
  const [open, setOpen] = useState(check.status !== 'pass');
  const s = STATUS[check.status] || STATUS.warn;
  const hasDetails = check.details && Object.keys(check.details).length > 0;
  return (
    <div style={{ borderTop: '1px solid #f1f5f9' }}>
      <div onClick={() => hasDetails && setOpen(!open)} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 20px', cursor: hasDetails ? 'pointer' : 'default' }}>
        <s.icon size={18} color={s.color} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{check.name}</span>
            <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>{check.durationMs} ms</span>
          </div>
          <div style={{ fontSize: 13, color: '#475569', marginTop: 3, wordBreak: 'break-word' }}>{check.message}</div>
          {open && hasDetails && (
            <pre style={{ marginTop: 10, padding: 12, background: '#0f172a', color: '#cbd5e1', borderRadius: 10, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(check.details, null, 2)}
            </pre>
          )}
        </div>
        {hasDetails && (open ? <FiChevronDown color="#94a3b8" /> : <FiChevronRight color="#94a3b8" />)}
      </div>
    </div>
  );
};

const SystemHealth = () => {
  const [data, setData] = useState(null);
  const [run, setRun] = useState(null); // run currently displayed
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const sendTest = async (e) => {
    e.preventDefault();
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await systemHealthAPI.sendTestEmail(testTo.trim());
      setTestResult({ ok: true, text: data.message, response: data.response });
    } catch (err) {
      setTestResult({ ok: false, text: err.response?.data?.message || 'Sending failed' });
    }
    setTesting(false);
  };

  const load = async () => {
    try {
      const { data: d } = await systemHealthAPI.get();
      setData(d);
      setRun(d.latest);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load system health');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: d } = await systemHealthAPI.run();
      toast.success(`Health check complete: ${d.run.status}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Health check failed to run');
    }
    setRunning(false);
  };

  const showRun = async (id) => {
    try {
      const { data: d } = await systemHealthAPI.getRun(id);
      setRun(d.run);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error('Failed to load that run');
    }
  };

  const sendAlert = async () => {
    try {
      const { data: d } = await systemHealthAPI.sendAlert(run._id);
      toast.success(d.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send alert');
    }
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>Loading system health…</div>;

  const overall = run ? OVERALL[run.status] : null;
  const grouped = {};
  (run?.checks || []).forEach(c => { (grouped[c.category] = grouped[c.category] || []).push(c); });
  const categories = Object.keys(grouped).sort((a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b));
  const isLatest = run && data?.latest && run._id === data.latest._id;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>System Health</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Database, payments, email, AI, users, workspaces and API checks</p>
        </div>
        <button onClick={runNow} disabled={running} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: running ? '#94a3b8' : '#00d4aa', color: '#020617', border: 'none', borderRadius: 10, fontWeight: 700, cursor: running ? 'not-allowed' : 'pointer', fontSize: 14 }}>
          <FiRefreshCw size={16} style={running ? { animation: 'spin 1s linear infinite' } : undefined} /> {running ? 'Running checks…' : 'Run checks now'}
        </button>
      </div>
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>

      {/* Overall status + schedule */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div style={{ ...card, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, gridColumn: 'span 2', borderLeft: `4px solid ${overall?.color || '#94a3b8'}` }}>
          {overall ? <overall.icon size={32} color={overall.color} /> : <FiActivity size={32} color="#94a3b8" />}
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{overall ? overall.label : 'No checks have run yet'}</div>
            {run && (
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                {run.summary.pass} passed · {run.summary.warn} warnings · {run.summary.fail} failed · {run.trigger} run {timeAgo(run.createdAt)}
                {run.triggeredBy ? ` by ${run.triggeredBy.firstName}` : ''} · {(run.durationMs / 1000).toFixed(1)}s
                {!isLatest && <strong style={{ color: '#3b82f6' }}> (viewing an older run)</strong>}
              </div>
            )}
          </div>
        </div>
        <div style={{ ...card, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: '#3b82f615', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiClock size={20} color="#3b82f6" /></div>
          <div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Automatic check</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{data?.schedule ? describeCron(data.schedule.cron, data.schedule.timezone) : '—'}</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>Last automatic run: {timeAgo(data?.schedule?.lastScheduledAt)}</div>
          </div>
        </div>
      </div>

      {run?.summary?.fail > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -12, marginBottom: 16 }}>
          <button onClick={sendAlert} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            <FiMail size={14} /> Email this report to super admins
          </button>
        </div>
      )}

      {/* Email delivery test */}
      <form onSubmit={sendTest} style={{ ...card, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <FiMail size={18} color="#3b82f6" />
        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Test email delivery</div>
        <input type="email" required value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="name@example.com"
          style={{ flex: '1 1 220px', maxWidth: 320, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
        <button type="submit" disabled={testing} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: testing ? '#94a3b8' : '#3b82f6', color: '#fff', fontWeight: 600, fontSize: 13, cursor: testing ? 'not-allowed' : 'pointer' }}>
          {testing ? 'Sending…' : 'Send test email'}
        </button>
        {testResult && (
          <div style={{ flexBasis: '100%', fontSize: 13, color: testResult.ok ? '#166534' : '#991b1b' }}>
            {testResult.text}{testResult.response ? <span style={{ color: '#94a3b8' }}> · server reply: {testResult.response}</span> : null}
          </div>
        )}
      </form>

      {/* Checks by category */}
      {categories.map(cat => {
        const st = grouped[cat].some(c => c.status === 'fail') ? 'fail' : grouped[cat].some(c => c.status === 'warn') ? 'warn' : 'pass';
        return (
          <div key={cat} style={{ ...card, marginBottom: 16, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: '#f8fafc' }}>
              <span style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{cat}</span>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS[st].color }} />
            </div>
            {grouped[cat].map(c => <CheckRow key={c.key} check={c} />)}
          </div>
        );
      })}

      {/* History */}
      {data?.history?.length > 0 && (
        <div style={{ ...card, marginTop: 24, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', fontWeight: 800, fontSize: 15, color: '#0f172a', background: '#f8fafc' }}>History (last 30 runs, kept 90 days)</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>{['When', 'Trigger', 'Status', 'Pass', 'Warn', 'Fail', 'Duration'].map(h => <th key={h} style={{ textAlign: 'left', padding: '10px 16px', color: '#64748b', fontWeight: 600, fontSize: 12, background: '#f8fafc' }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.history.map(h => (
                  <tr key={h._id} onClick={() => showRun(h._id)} style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer', background: run?._id === h._id ? '#f0fdfa' : undefined }}>
                    <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{new Date(h.createdAt).toLocaleString()}</td>
                    <td style={{ padding: '10px 16px', textTransform: 'capitalize' }}>{h.trigger}</td>
                    <td style={{ padding: '10px 16px' }}><span style={{ fontWeight: 700, color: OVERALL[h.status]?.color, textTransform: 'capitalize' }}>{h.status}</span></td>
                    <td style={{ padding: '10px 16px', color: STATUS.pass.color }}>{h.summary?.pass}</td>
                    <td style={{ padding: '10px 16px', color: STATUS.warn.color }}>{h.summary?.warn}</td>
                    <td style={{ padding: '10px 16px', color: STATUS.fail.color }}>{h.summary?.fail}</td>
                    <td style={{ padding: '10px 16px', color: '#64748b' }}>{(h.durationMs / 1000).toFixed(1)}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealth;
